import React, { useState } from "react";

import {
  calculateFootprintingProgress,
  getYearArray,
  useGetCarbonProgramByFarmId,
  useGetFootprintingCropFootprints,
  useGetFootprintingFarmFootprints,
  useNavigate,
  useTranslation,
  useUserContext,
} from "utils";

import { Flex, MainPage, MetaData, PageSpin, TabLabel, Tabs } from "components";

import FootprintingProgressWidget from "pages/app/footprinting/components/FootprintingProgressWidget";
import { FootprintingYearPage } from "pages/app/footprinting/components/FootprintingYearPage";

const Footprinting: React.FC = () => {
  const { t } = useTranslation([]);
  const { user } = useUserContext();
  const navigate = useNavigate();

  const farmId = user?.farm.id ?? undefined;
  const carbonProgramQuery = useGetCarbonProgramByFarmId(farmId);

  const currentYear = new Date().getFullYear();

  const availableYears = getYearArray(carbonProgramQuery.data?.farmLevelFootprinting?.startYear ?? currentYear, currentYear).filter(
    (year) => !carbonProgramQuery.data?.farmLevelFootprinting?.disabledYears?.includes(year)
  );
  const [selectedYear, setSelectedYear] = useState(carbonProgramQuery.data?.farmLevelFootprinting?.startYear ?? currentYear);

  const farmProgressQueries = useGetFootprintingFarmFootprints(farmId, availableYears);
  const cropProgressQueries = useGetFootprintingCropFootprints(farmId, availableYears, carbonProgramQuery.data?.farmLevelFootprinting?.programCrops);

  if (!user?.farm || !user?.farm?.id || carbonProgramQuery.isLoading || !carbonProgramQuery.data) {
    return (
      <Flex justify="center">
        <PageSpin />
      </Flex>
    );
  }

  if (carbonProgramQuery.data.farmLevelFootprinting === undefined || carbonProgramQuery.data.platformFeatures.footprinting?.enabled === false) {
    navigate("/home");
  }

  const navigationCrumbs = {
    crumbs: [
      { name: t("global:header.home"), link: "/home" },
      { name: user?.farm?.name || t("modules:navigation.overview.farm") },
      { name: t("footprinting:footprintingLandingPage.breadcrumb") },
    ],
  };

  const yearsToIsSubmitted = availableYears.reduce(
    (acc, year) => {
      acc[year] = calculateFootprintingProgress(
        farmProgressQueries.find((query) => query.data?.year === year)?.data,
        // @ts-expect-error Not sure why TS doesn't understand that the filter ensures data is defined
        cropProgressQueries.filter((query) => !!query.data && query.data?.year === year).map((q) => q.data)
      ).isSubmitted;
      return acc;
    },
    {} as Record<number, boolean>
  );

  return (
    <MainPage pageName="Footprinting" navigationCrumbs={navigationCrumbs} showMobileAlert fullWidth>
      <MetaData title={t("footprinting:footprintingPage")} />
      <Flex className="footprinting-sidebar container" justify="space-between">
        <Flex vertical className="main-container flex-1 margin-left-500 margin-right-500">
          <Tabs
            className="padding-top-200 padding-left-300"
            activeKey={selectedYear.toString()}
            size="large"
            onChange={(k) => setSelectedYear(parseInt(k))}
            items={availableYears.map((y) => ({
              label: (
                <TabLabel dotColor="orange" showDot={!yearsToIsSubmitted[y]}>
                  {y.toString()}
                </TabLabel>
              ),
              key: y.toString(),
              children: (
                <FootprintingYearPage
                  year={y}
                  farmId={user.farm.id ?? ""}
                  cropsWithFootprintForYear={cropProgressQueries
                    .filter((cpq) => cpq.data !== undefined && cpq.data.footprint !== undefined && cpq.data.year === selectedYear)
                    .map((cpq) => cpq.data?.cropId ?? "")}
                  yearIsSubmitted={yearsToIsSubmitted[y]}
                />
              ),
            }))}
          />
        </Flex>
        <Flex className="sidebar side-bar-footprinting full-width" vertical>
          <FootprintingProgressWidget carbonProgram={carbonProgramQuery.data} farmId={user.farm.id ?? ""} year={selectedYear} />
        </Flex>
      </Flex>
    </MainPage>
  );
};

export default Footprinting;
